import { create } from "zustand";

export type OrganizationAccountRole = "owner" | "admin" | "operator";

export type AccountContext = {
  id: string;
  kind: "organization" | "location_contact";
  organizationId: string | null;
  organizationName: string | null;
  accountMode: "lead_only" | "full" | null;
  role: OrganizationAccountRole | "location_contact";
  canViewCalendar?: boolean;
  canLinkExpenseAssociations?: boolean;
  locationCount?: number;
  organizationCount?: number;
};

type AccountContextsResponse = {
  contexts?: unknown;
  default_context_id?: string | null;
  defaultContextId?: string | null;
};

type AccountContextApiRow = Partial<AccountContext> & {
  organization_id?: string | null;
  organization_name?: string | null;
  account_mode?: "lead_only" | "full" | null;
  can_view_calendar?: boolean;
  can_link_expense_associations?: boolean;
  location_count?: number | null;
  organization_count?: number | null;
};

const accountContextFromApi = (value: unknown): AccountContext | null => {
  if (!value || typeof value !== "object") return null;
  const row = value as AccountContextApiRow;
  if (typeof row.id !== "string" || !row.id || typeof row.kind !== "string" || typeof row.role !== "string") return null;
  const organizationId = row.organizationId ?? row.organization_id ?? null;
  const isOrganization = row.kind === "organization";
  const canLinkExpenseAssociations = row.canLinkExpenseAssociations ?? row.can_link_expense_associations;
  if (row.kind !== "organization" && row.kind !== "location_contact") return null;
  if (isOrganization && (!organizationId || row.id !== `organization:${organizationId}` || !["owner", "admin", "operator"].includes(row.role))) return null;
  if (!isOrganization && (row.id !== "location_contact" || row.role !== "location_contact")) return null;
  return {
    id: row.id,
    kind: row.kind,
    organizationId: isOrganization ? organizationId : null,
    organizationName: isOrganization ? row.organizationName ?? row.organization_name ?? null : null,
    accountMode: isOrganization ? row.accountMode ?? row.account_mode ?? null : null,
    role: row.role,
    ...(canLinkExpenseAssociations !== undefined ? { canLinkExpenseAssociations: isOrganization && canLinkExpenseAssociations === true } : {}),
    canViewCalendar: isOrganization && (row.canViewCalendar ?? row.can_view_calendar) === true,
    locationCount: row.locationCount ?? row.location_count ?? undefined,
    organizationCount: row.organizationCount ?? row.organization_count ?? undefined,
  };
};

type AccountContextState = {
  contexts: AccountContext[];
  selectedContextId: string | null;
  hydrated: boolean;
  switching: boolean;
  requestHeadersEnabled: boolean;
  setContexts: (
    contexts: AccountContext[],
    selectedContextId: string | null,
    requestHeadersEnabled?: boolean,
  ) => void;
  setSelectedContextId: (selectedContextId: string) => void;
  updateOrganizationName: (organizationId: string, organizationName: string) => void;
  setSwitching: (switching: boolean) => void;
  reset: () => void;
};

const STORAGE_KEY = "vendistri.accountContext";
let contextRevision = 0;

const readStoredContextId = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (_error) {
    return null;
  }
};

const persistContextId = (contextId: string | null) => {
  try {
    if (contextId) window.localStorage.setItem(STORAGE_KEY, contextId);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    // Ignore unavailable storage.
  }
};

export const useAccountContextStore = create<AccountContextState>((set) => ({
  contexts: [],
  selectedContextId: readStoredContextId(),
  hydrated: false,
  switching: false,
  requestHeadersEnabled: false,
  setContexts: (contexts, selectedContextId, requestHeadersEnabled = true) => {
    contextRevision += 1;
    persistContextId(selectedContextId);
    set({ contexts, selectedContextId, hydrated: true, requestHeadersEnabled });
  },
  setSelectedContextId: (selectedContextId) => {
    contextRevision += 1;
    persistContextId(selectedContextId);
    set({ selectedContextId });
  },
  updateOrganizationName: (organizationId, organizationName) =>
    set((state) => ({
      contexts: state.contexts.map((context) =>
        context.kind === "organization" &&
        context.organizationId === organizationId
          ? { ...context, organizationName }
          : context,
      ),
    })),
  setSwitching: (switching) => set({ switching }),
  reset: () => {
    contextRevision += 1;
    persistContextId(null);
    set({
      contexts: [],
      selectedContextId: null,
      hydrated: false,
      switching: false,
      requestHeadersEnabled: false,
    });
  },
}));

export const selectedAccountContext = (
  state = useAccountContextStore.getState(),
) =>
  state.contexts.find((context) => context.id === state.selectedContextId) ||
  null;

/** Observe authority transitions without canceling an ordinary billing refresh. */
export function subscribeToAccountContextChange(onChange: () => void): () => void {
  const initial = useAccountContextStore.getState();
  const context = selectedAccountContext(initial);
  return useAccountContextStore.subscribe(state => {
    const current = selectedAccountContext(state);
    if (state.selectedContextId !== initial.selectedContextId ||
        state.requestHeadersEnabled !== initial.requestHeadersEnabled || state.switching !== initial.switching ||
        current?.kind !== context?.kind || current?.organizationId !== context?.organizationId ||
        current?.role !== context?.role) onChange();
  });
}

export const getAccountContextRequestHeaders = (): HeadersInit => {
  const state = useAccountContextStore.getState();
  const context = selectedAccountContext(state);
  if (!state.requestHeadersEnabled) return {};
  if (context?.kind === "location_contact") return { "X-Vendistri-Account-Context": "location_contact" };
  return context?.kind === "organization" && context.organizationId
    ? {
        "X-Vendistri-Organization": context.organizationId,
        "X-Vendistri-Account-Context": "true",
      }
    : {};
};

export async function loadAccountContexts(
  requestHeadersEnabled = true,
  { preservingSelection = false }: { preservingSelection?: boolean } = {},
): Promise<AccountContext | null> {
  const revision = ++contextRevision;
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL || ""}/user/account-contexts`,
    { credentials: "include" },
  );
  if (!response.ok) throw new Error("Failed to load account views");
  const payload = (await response.json()) as AccountContextsResponse;
  if (revision !== contextRevision) throw new DOMException("Account view changed while loading.", "AbortError");
  const contexts = Array.isArray(payload.contexts)
    ? payload.contexts
        .map(accountContextFromApi)
        .filter((context): context is AccountContext => Boolean(context))
    : [];
  const storedId = useAccountContextStore.getState().selectedContextId;
  if (preservingSelection && storedId !== null && !contexts.some(context => context.id === storedId)) {
    throw new Error("You no longer have access to this account.");
  }
  const defaultId = payload.defaultContextId || payload.default_context_id;
  const selectedContextId = preservingSelection || contexts.some((context) => context.id === storedId)
    ? storedId
    : contexts.find((context) => context.id === defaultId)?.id || contexts[0]?.id || null;
  useAccountContextStore
    .getState()
    .setContexts(contexts, selectedContextId, requestHeadersEnabled);
  return selectedAccountContext();
}

export const accountContextRoleFlags = (context: AccountContext | null) => ({
  isContact: context?.kind === "location_contact",
  isOwner: context?.kind === "organization" && context.role === "owner",
  isAdmin: context?.kind === "organization" && context.role === "admin",
  isOperator: context?.kind === "organization" && context.role === "operator",
  canViewCalendar: context?.kind === "organization" && (context.role === "owner" || context.role === "admin" || context.canViewCalendar === true),
  accountMode: context?.kind === "organization" ? context.accountMode : null,
  viewMode: context?.kind === "location_contact" ? "contact" as const : "org" as const,
});
